import { BarChart, Bar, Cell, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import type { AbcAnalysis, AbcClass } from "@/hooks/use-analytics";
import { formatCurrency } from "@/lib/utils";

type ParetoRow = AbcAnalysis["pareto"][number];
type Metric = "revenue" | "quantity";

// Iste boje kao trake u „Struktura asortimana" (bg-emerald-500 / bg-amber-500 / bg-slate-400),
// da isti proizvod nosi istu boju u oba bloka grida.
const ABC_COLORS: Record<AbcClass, string> = {
  A: "#10b981",
  B: "#f59e0b",
  C: "#94a3b8",
};

const LEGEND: { abcClass: AbcClass; label: string }[] = [
  { abcClass: "A", label: "A · do 80 %" },
  { abcClass: "B", label: "B · 80–95 %" },
  { abcClass: "C", label: "C · zadnjih 5 % i bez prodaje" },
];

function fmtValue(value: number, metric: Metric): string {
  return metric === "revenue" ? formatCurrency(value, "KM") : `${value} kom`;
}

function fmtTick(value: number, metric: Metric): string {
  if (value >= 1000000) return `${Math.round(value / 1000000)} mil.`;
  if (value >= 1000) return `${Math.round(value / 1000)} tis.`;
  return `${Math.round(value)}`;
}

export function AbcPareto({ pareto, metric }: { pareto: AbcAnalysis["pareto"]; metric: Metric }) {
  return (
    <div className="ring-1 ring-foreground/10">
      <div className="px-3 py-2.5 border-b flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-xs font-medium">
          Pareto — top 30 po {metric === "revenue" ? "prihodu" : "količini"}
        </span>
        <span className="text-xs text-muted-foreground">kumulativni udio u tooltipu</span>
        <div className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
          {LEGEND.map((l) => (
            <span key={l.abcClass} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="w-2 h-2 shrink-0 rounded-full" style={{ background: ABC_COLORS[l.abcClass] }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      <div className="px-3 py-3">
        {pareto.length === 0 ? (
          <p className="text-xs text-muted-foreground">Nema prodaje u odabranom periodu.</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pareto} margin={{ top: 4, right: 4, bottom: 0, left: 0 }} barCategoryGap={2}>
              <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.1} className="text-foreground" />
              <XAxis dataKey="name" tick={false} axisLine={{ stroke: "currentColor", strokeOpacity: 0.15 }}
                tickLine={false} height={8} className="text-foreground" />
              <YAxis width={52} tickLine={false} axisLine={false}
                tick={{ fontSize: 11, fill: "currentColor", fillOpacity: 0.6 }}
                tickFormatter={(v: number) => fmtTick(v, metric)} className="text-foreground" />
              <Tooltip
                cursor={{ fill: "currentColor", fillOpacity: 0.06 }}
                content={({ active, payload }) => {
                  const row: ParetoRow | undefined = payload?.[0]?.payload;
                  if (!active || !row) return null;
                  return (
                    <div className="bg-background ring-1 ring-foreground/15 shadow-sm px-2.5 py-1.5 flex flex-col gap-0.5 max-w-[220px]">
                      <span className="text-xs font-semibold tabular-nums">{fmtValue(row.value, metric)}</span>
                      <span className="text-xs text-muted-foreground">{row.name}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        kumulativno {row.cumulativeShare}%
                      </span>
                    </div>
                  );
                }}
              />
              <Bar dataKey="value" maxBarSize={24} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                {pareto.map((p) => (
                  <Cell key={p.productId} fill={ABC_COLORS[p.abcClass]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
